import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import type { AdherenceLog, AdherenceLogEventType, EventAlert } from './events-client.type';

const axiosInstance = AxiosInstance();
const eventAlertBaseURL = import.meta.env.VITE_APP_API_URL + '/api/event-tracking/';
const adherenceLogBaseURL = import.meta.env.VITE_APP_API_URL + '/api/adherence-logs/';
/**
 * Creates a new event alert
 *
 * @param {Partial<EventAlert>} eventAlert - The event alert data to create
 * @returns {Promise<EventAlert>} - A promise that resolves to the created event alert
 */
const createEventAlert = async (eventAlert: Partial<EventAlert>): Promise<EventAlert> => {
  return await axiosInstance
    .post(eventAlertBaseURL, eventAlert)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while creating event alert'));
};

/**
 * Updates an existing event alert
 *
 * @param {Partial<EventAlert>} eventAlert - The event alert data to update (must include id)
 * @returns {Promise<EventAlert>} - A promise that resolves to the updated event alert
 */
const updateEventAlert = async (eventAlert: Partial<EventAlert>): Promise<EventAlert> => {
  if (!eventAlert.id) {
    return Promise.reject('Event alert ID is required');
  }
  return await axiosInstance
    .put(`${eventAlertBaseURL}${eventAlert.id}/`, eventAlert)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while updating event alert'));
};

/**
 * Deletes an event alert by ID
 *
 * @param {string} eventAlertId - The ID of the event alert to delete
 * @returns {Promise<void>} - A promise that resolves when the event alert is deleted
 */
const deleteEventAlert = async (eventAlertId: string): Promise<void> => {
  return await axiosInstance
    .delete(`${eventAlertBaseURL}${eventAlertId}/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while deleting event alert'));
};

/**
 * Fetches event alerts for a given step
 *
 * @param {string} stepId - The ID of the step to fetch event alerts for
 * @returns {Promise<EventAlert[]>} - A promise that resolves to the event alerts for the step
 */
const getStepEventAlerts = async (stepId: string): Promise<EventAlert[]> => {
  return await axiosInstance
    .get(`${eventAlertBaseURL}?step_id=${stepId}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching step event alerts'));
};

/**
 * Fetches adherence logs for a given organization and event type
 *
 * @param {string} organizationId - The ID of the organization to fetch adherence logs for
 * @param {AdherenceLogEventType[]} eventTypes - The types of events to fetch adherence logs for
 * @returns {Promise<AdherenceLog[]>} - A promise that resolves to the adherence logs for the organization and event type
 */
const getAdherenceLogs = async (
  organizationId: string,
  eventTypes?: AdherenceLogEventType[],
): Promise<AdherenceLog[]> => {
  const queryParams = new URLSearchParams();
  queryParams.set('organization_id', organizationId);
  if (eventTypes) {
    queryParams.set('event_types', eventTypes.join(','));
  }
  return await axiosInstance
    .get(`${adherenceLogBaseURL}?${queryParams.toString()}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching adherence logs'));
};

/**
 * Deletes an adherence log by ID
 *
 * @param {string} adherenceLogId - The ID of the adherence log to delete
 * @returns {Promise<void>} - A promise that resolves when the adherence log is deleted
 */
const deleteAdherenceLog = async (adherenceLogId: string): Promise<void> => {
  return await axiosInstance
    .delete(`${adherenceLogBaseURL}${adherenceLogId}/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while deleting adherence log'));
};

export {
  createEventAlert,
  deleteAdherenceLog,
  deleteEventAlert,
  getAdherenceLogs,
  getStepEventAlerts,
  updateEventAlert,
};
